/**
 * Invoice detail screen (WO-M4).
 *
 * Shows InvoiceDetail: line items (snapshot), payment history, balance_due.
 *
 * Payment link flow:
 *   "Pay / Get payment link" → POST /api/invoices/:id/payment-link
 *   status 'created'      → open url via openExternalUrl shim (Stripe hosted page; new tab on web)
 *   status 'unconfigured' → show "Online payments not yet configured" message, NO crash
 *
 * invoiceId is an opaque UUID path param — no PII in URL (mobile CLAUDE.md §2).
 * expo-web-browser opens a Stripe hosted URL; no card data ever passes through the app
 * (mobile CLAUDE.md §2 + backend CLAUDE.md invariant 11).
 *
 * WO-M0 refactor — rebuilt on the design system (Screen / Card / Badge / Button / Text + tokens).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { openExternalUrl } from '../../../src/platform/linking';
import { downloadAndSharePdf } from '../../../src/platform/savePdf';
import { getInvoice, createPaymentLink } from '../../../src/api/billing';
import { ApiRequestError, getAccessToken } from '../../../src/api/client';
import { API_BASE_URL } from '../../../src/config';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  invoiceBadge,
} from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { humanize, formatQuantity } from '../../../src/lib/format';
import { CoachmarkOverlay } from '../../../src/components/help';
import { FLOW_KEYS } from '../../../src/help';
import type { InvoiceLineItemDto, PaymentSummary } from '@beam/api-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string | null | undefined, currency = 'USD'): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return 'An unexpected error occurred.';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InvoiceLineRow({ item, currency }: { item: InvoiceLineItemDto; currency: string }) {
  return (
    <View style={styles.lineRow}>
      <View style={styles.lineDesc}>
        {item.section ? (
          <Text variant="label" color="secondary">
            {item.section}
          </Text>
        ) : null}
        <Text variant="bodyStrong">{item.description}</Text>
        <Text variant="small" color="secondary">
          {humanize(item.lineType)} · qty {formatQuantity(item.quantity)} × {formatCurrency(item.unitPrice, currency)}
        </Text>
      </View>
      <Text variant="data" style={styles.lineTotal}>
        {item.lineTotal ? formatCurrency(item.lineTotal, currency) : '—'}
      </Text>
    </View>
  );
}

function PaymentRow({ payment, currency }: { payment: PaymentSummary; currency: string }) {
  return (
    <View style={styles.paymentRow}>
      <View style={styles.paymentInfo}>
        <Text variant="bodyStrong">{payment.method}</Text>
        {payment.receivedDate ? (
          <Text variant="small" color="secondary">
            {new Date(payment.receivedDate).toLocaleDateString()}
          </Text>
        ) : null}
      </View>
      <Text variant="data" color="link">
        {formatCurrency(payment.amount, currency)}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function InvoiceDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [payLinkLoading, setPayLinkLoading] = useState(false);
  const [payLinkError, setPayLinkError] = useState<string | null>(null);
  const [unconfiguredMessage, setUnconfiguredMessage] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['invoices', id],
    queryFn: () => getInvoice(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  // ---------------------------------------------------------------------------
  // Payment link handler
  // ---------------------------------------------------------------------------
  async function handlePaymentLink() {
    if (!id) return;
    setPayLinkLoading(true);
    setPayLinkError(null);
    setUnconfiguredMessage(false);

    try {
      const result = await createPaymentLink(id);

      if (result.status === 'created' && result.url) {
        // Open the hosted Stripe Checkout (on the org's OWN connected account) in the device
        // browser. No card data passes through the app — Stripe owns PCI scope.
        await openExternalUrl(result.url);
      } else if (result.status === 'not_connected') {
        // The org hasn't connected its own Stripe account yet → point the owner to Payments
        // settings; manual payment recording still works.
        setUnconfiguredMessage(true);
      } else if (result.status === 'already_paid') {
        await refetch();
      }
    } catch (err) {
      setPayLinkError(extractErrorMessage(err));
    } finally {
      setPayLinkLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Download / Share branded PDF (GET /api/invoices/:id/pdf, authed binary)
  // ---------------------------------------------------------------------------
  async function handleDownloadPdf() {
    if (!id) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const token = getAccessToken();
      const fileName = `invoice-${(data?.invoiceNumber ?? id).replace(/[^\w.-]/g, '_')}.pdf`;
      await downloadAndSharePdf(
        `${API_BASE_URL}/api/invoices/${id}/pdf`,
        fileName,
        token ? { Authorization: `Bearer ${token}` } : {},
      );
    } catch (err) {
      setPdfError(extractErrorMessage(err));
    } finally {
      setPdfLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guard: no ID
  // ---------------------------------------------------------------------------
  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No invoice ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading invoice"
          />
          <Text variant="body" color="secondary">
            Loading…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error)}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading invoice"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  if (!data) return null;

  const b = invoiceBadge(data.status);
  const balanceDuePositive = data.balanceDue != null && parseFloat(data.balanceDue) > 0;

  return (
    <Screen scroll>
      <CoachmarkOverlay flowKey={FLOW_KEYS.sendInvoice} />
      {/* Invoice status badge + heading */}
      <View style={styles.statusRow}>
        <Text variant="title">Invoice</Text>
        <Badge status={b.key} label={b.label} />
      </View>

      {/* Summary card */}
      <Card>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Invoice #
          </Text>
          <Text variant="data">
            {data.invoiceNumber ?? data.id.slice(0, 8).toUpperCase()}
          </Text>
        </View>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Type
          </Text>
          <Text variant="data">{humanize(data.type)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Currency
          </Text>
          <Text variant="data">{data.currency}</Text>
        </View>
        {data.issuedDate ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Issued
            </Text>
            <Text variant="data">{new Date(data.issuedDate).toLocaleDateString()}</Text>
          </View>
        ) : null}
        {data.dueDate ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Due
            </Text>
            <Text variant="data">{new Date(data.dueDate).toLocaleDateString()}</Text>
          </View>
        ) : null}
      </Card>

      {/* Line items */}
      {data.lineItems.length > 0 ? (
        <View style={styles.section}>
          <Text variant="label" color="secondary">
            Line Items
          </Text>
          <Card style={styles.lineItemsCard}>
            {data.lineItems
              .slice()
              .sort((a: InvoiceLineItemDto, b: InvoiceLineItemDto) => a.sortOrder - b.sortOrder)
              .map((item: InvoiceLineItemDto) => (
                <InvoiceLineRow key={item.id} item={item} currency={data.currency} />
              ))}
          </Card>
        </View>
      ) : (
        <Card>
          <Text variant="body" color="secondary">
            No line items.
          </Text>
        </Card>
      )}

      {/* Totals card */}
      <Card>
        {data.subtotal ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Subtotal
            </Text>
            <Text variant="data">{formatCurrency(data.subtotal, data.currency)}</Text>
          </View>
        ) : null}
        {data.discountTotal && data.discountTotal !== '0.00' ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Discount
            </Text>
            <Text variant="data" color="link">
              −{formatCurrency(data.discountTotal, data.currency)}
            </Text>
          </View>
        ) : null}
        {data.taxAmount && data.taxAmount !== '0.00' ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Tax
            </Text>
            <Text variant="data">{formatCurrency(data.taxAmount, data.currency)}</Text>
          </View>
        ) : null}
        <View style={[styles.cardRow, styles.grandTotalRow]}>
          <Text variant="bodyStrong">Total</Text>
          <Text variant="amount">{formatCurrency(data.total, data.currency)}</Text>
        </View>
        {data.amountPaid ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Paid
            </Text>
            <Text variant="data" color="link">
              {formatCurrency(data.amountPaid, data.currency)}
            </Text>
          </View>
        ) : null}
        {data.balanceDue ? (
          <View style={[styles.cardRow, styles.balanceDueRow]}>
            <Text variant="bodyStrong">Balance Due</Text>
            <Text
              variant="amount"
              color={balanceDuePositive ? 'danger' : 'link'}
            >
              {formatCurrency(data.balanceDue, data.currency)}
            </Text>
          </View>
        ) : null}
      </Card>

      {/* Payment history */}
      {data.payments.length > 0 ? (
        <View style={styles.section}>
          <Text variant="label" color="secondary">
            Payments
          </Text>
          <Card style={styles.lineItemsCard}>
            {data.payments.map((payment: PaymentSummary) => (
              <PaymentRow key={payment.id} payment={payment} currency={data.currency} />
            ))}
          </Card>
        </View>
      ) : null}

      {/* Download / Share branded PDF — available for any invoice */}
      <View style={styles.paySection}>
        {pdfError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{pdfError}</Text>
          </Card>
        ) : null}
        <Button
          title="Download / Share PDF"
          variant="secondary"
          size="lg"
          loading={pdfLoading}
          disabled={pdfLoading}
          onPress={handleDownloadPdf}
          accessibilityLabel="Download or share invoice PDF"
        />
      </View>

      {/* Payment link button + feedback */}
      {balanceDuePositive ? (
        <View style={styles.paySection}>
          {/* Unconfigured notice */}
          {unconfiguredMessage ? (
            <Card>
              <Text variant="body" color="secondary" accessibilityRole="alert">
                Card payments aren't set up yet. Connect your Stripe account to let customers pay this
                invoice online. Until then you can record a cash or check payment.
              </Text>
              <Button
                title="Set up card payments"
                variant="primary"
                size="sm"
                fullWidth={false}
                onPress={() => router.push('/(app)/account/payments')}
                accessibilityLabel="Set up card payments"
              />
              <Button
                title="Dismiss"
                variant="secondary"
                size="sm"
                fullWidth={false}
                onPress={() => {
                  setUnconfiguredMessage(false);
                  setPayLinkError(null);
                }}
                accessibilityLabel="Dismiss payment message"
              />
            </Card>
          ) : null}

          {/* API error */}
          {payLinkError ? (
            <Card>
              <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
                {payLinkError}
              </Text>
            </Card>
          ) : null}

          {/* Pay button — primary forward action, NOT owner-gated */}
          {!unconfiguredMessage ? (
            <Button
              title="Pay / Get Payment Link"
              variant="primary"
              size="lg"
              loading={payLinkLoading}
              disabled={payLinkLoading}
              onPress={handlePaymentLink}
              accessibilityLabel="Get payment link"
            />
          ) : null}
        </View>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  grandTotalRow: {
    paddingTop: theme.spacing.md,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    marginTop: theme.spacing.xs,
  },
  balanceDueRow: {
    paddingTop: theme.spacing.sm,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    marginTop: theme.spacing.xs,
  },
  section: {
    gap: theme.spacing.sm,
  },
  lineItemsCard: {
    gap: 0,
    padding: 0,
    overflow: 'hidden',
  },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
    gap: theme.spacing.sm,
  },
  lineDesc: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  lineTotal: {
    minWidth: 70,
    textAlign: 'right',
  },
  paymentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
    gap: theme.spacing.sm,
  },
  paymentInfo: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  paySection: {
    gap: theme.spacing.sm,
  },
});
